import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSnapshot, type FetchResult, type SourceKind } from '../data/client';
import type { RangeKey } from '../data/types';

/**
 * How often a *visible* tab re-fetches. Cheap enough to leave open all day
 * against the ingest service, frequent enough that the numbers on screen are
 * never more than half a minute stale.
 */
export const REFRESH_INTERVAL_MS = 30_000;

interface State {
  data: FetchResult | null;
  loading: boolean;
  error: string | null;
  /** A background refresh is in flight. Never blanks what is already on screen. */
  refreshing: boolean;
  /** epoch ms of the last fetch that actually produced data. */
  updatedAt: number | null;
  /** epoch ms the next background poll is due, or null while paused. */
  nextAt: number | null;
}

/**
 * The snapshot, kept fresh in the background.
 *
 * Two kinds of fetch share one code path:
 *
 *   - Foreground — a mount, or a change of range or token. Nothing usable is on
 *     screen, so it sets `loading` and the page shows a skeleton.
 *   - Background — the poll below. It never sets `loading`, never clears the
 *     data, and never downgrades a live snapshot to the mock fallback, because
 *     all three would make a silent refresh look like a page reload. A failed
 *     background poll is simply ignored; the next one tries again.
 *
 * Polling stops while the tab is hidden — a backgrounded dashboard should not
 * keep a phone or a laptop awake — and catches up on the way back if the data
 * went stale in the meantime.
 *
 * Signing in needs no nonce here: the gate lives above the component that calls
 * this, so a successful sign-in mounts a fresh hook rather than re-running one.
 */
export function useSnapshot(range: RangeKey): State {
  const [state, setState] = useState<State>({
    data: null,
    loading: true,
    error: null,
    refreshing: false,
    updatedAt: null,
    nextAt: null,
  });

  // Held in a ref so the poll can abort an in-flight request when the range
  // changes under it, without the interval depending on the request itself.
  const inflight = useRef<AbortController | null>(null);
  const updatedAt = useRef<number | null>(null);
  const lastSource = useRef<SourceKind | null>(null);

  const load = useCallback(async (background: boolean) => {
    inflight.current?.abort();
    const controller = new AbortController();
    inflight.current = controller;

    setState((s) => (background ? { ...s, refreshing: true } : { ...s, loading: true }));

    try {
      const data = await fetchSnapshot(range, controller.signal);
      if (controller.signal.aborted) return;

      // A background poll that could not reach the API keeps the last good
      // snapshot. Swapping real numbers for generated ones mid-session is
      // exactly the visible "refresh" this is meant to avoid.
      const downgrade =
        background &&
        data.source === 'fallback' &&
        lastSource.current !== null &&
        lastSource.current !== 'fallback';
      if (downgrade) {
        setState((s) => ({ ...s, refreshing: false }));
        return;
      }

      const now = Date.now();
      lastSource.current = data.source;
      updatedAt.current = now;
      // Spread the previous state so the poll's own `nextAt` is not wiped by the
      // result of the poll it scheduled.
      setState((s) => ({ ...s, data, loading: false, error: null, refreshing: false, updatedAt: now }));
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : String(err);
      setState((s) =>
        background
          ? { ...s, refreshing: false } // keep showing what we have
          : { ...s, data: null, loading: false, error: message, refreshing: false, updatedAt: null },
      );
    }
  }, [range]);

  // Foreground load on mount and whenever the range changes.
  useEffect(() => {
    void load(false);
    return () => inflight.current?.abort();
  }, [load]);

  /*
   * Background poll, paused while the tab is hidden.
   *
   * A self-rescheduling timeout rather than setInterval, because the countdown
   * in the sidebar is driven off `nextAt` and has to stay honest: a fixed
   * interval keeps ticking while a slow request is still in flight, so the
   * clock would reach zero and sit there. Chaining the next timeout to the end
   * of each poll means "next update in Ns" is always a real deadline.
   */
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const schedule = (delay: number) => {
      if (cancelled || document.hidden) return;
      const at = Date.now() + delay;
      setState((s) => (s.nextAt === at ? s : { ...s, nextAt: at }));
      timer = setTimeout(() => {
        void load(true).finally(() => schedule(REFRESH_INTERVAL_MS));
      }, delay);
    };

    const stop = () => {
      clearTimeout(timer);
      timer = undefined;
      setState((s) => (s.nextAt === null ? s : { ...s, nextAt: null }));
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
        return;
      }
      // Catch up before resuming the cadence, so coming back to a tab left open
      // over lunch does not show stale numbers until the next tick.
      const age = updatedAt.current === null ? Infinity : Date.now() - updatedAt.current;
      schedule(age >= REFRESH_INTERVAL_MS ? 0 : REFRESH_INTERVAL_MS - age);
    };

    schedule(REFRESH_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load]);

  return state;
}
